import React, { useState } from 'react'
import DataGrid, {
	Editing,
	Scrolling,
	RemoteOperations,
	Column,
	Form,
	Lookup,
	SearchPanel,
	RequiredRule,
	EmailRule,
	HeaderFilter,
	FilterPanel,
	FilterRow,
	Paging,
	Pager,
	GroupPanel,
	AsyncRule,
	ColumnChooser
} from 'devextreme-react/data-grid'
import { Item } from 'devextreme-react/form'

import { confirm } from 'devextreme/ui/dialog'
import { DataView, DataStyles } from 'app/data-grid'
import apiService from 'app/services/apiService'
import ApplicationTagBoxComponent from './ApplicationTagBoxComponent.js'

const dataSource = apiService.createDataCustomStore('UserID', 'users')
const applications = apiService.createRowCustomStore('ApplicationID', 'applications')
const companies = apiService.createRowCustomStore('CompanyID', 'companies')
const departments = apiService.createRowCustomStore('DepartmentID', 'departments')
const users = apiService.createRowCustomStore('UserID', 'users')

const cellApplicationsTemplate = (container, options) => {
	const noBreakSpace = '\u00A0',
		text = Object.keys(options.value || [])
			.map(k => {
				return options.value[k]
			})
			.join(', ')
	container.textContent = text || noBreakSpace
	container.title = text
}

const cellIDTemplate = (container, options) => {
	container.textContent = Object.keys(options.data).length !== 0 ? options.value : 'Auto Generated'
}

const handleResetPassword = userData => {
	// Show DevExtreme confirmation dialog
	confirm(`Are you sure you want to reset the password for user "${userData.Username}"?`, 'Reset Password').then(
		dialogResult => {
			if (dialogResult) {
				// TODO: Implement password reset logic
				console.log('Reset password for user:', userData.UserID, userData.Username)
				// You can add your password reset API call here
				apiService.sendNotification(userData.Username)

				// Show success message using DevExtreme notify
				import('devextreme/ui/notify').then(({ default: notify }) => {
					notify({
						message: `Password reset initiated for user "${userData.Username}"`,
						type: 'success',
						displayTime: 3000,
						position: 'top right'
					})
				})
			}
		}
	)
}

const Users = () => {
	const classes = DataStyles()

	const asyncValidation = params => {
		const { data, value } = params
		const { UserID } = data

		// if (UserID) {
		// 	return true
		// }
		// eslint-disable-next-line react/no-unused-state
		return apiService.sendRequest(value, UserID)
	}

	const [isAdding, setIsAdding] = useState(true)

	const onInitNewRow = e => {
		e.data.IsActive = true
		e.data.IsAzureSSO = false
		e.data.Is2FAConfigured = false
		e.data.IsPinEnabled = false
		setIsAdding(true)
	}

	const onEditStart = e => {
		setIsAdding(false)
	}

	const onEditStop = e => {
		setIsAdding(true)
	}

	const onRowPrepared = e => {
		if (e.rowType === 'data') {
			// Add reset password button to the actions cell
			const actionsCell = e.rowElement.querySelector('.dx-command-edit')
			if (actionsCell && !actionsCell.querySelector('.reset-password-btn')) {
				// Find the edit button specifically
				const editButton = actionsCell.querySelector('.dx-link-edit')

				const resetBtn = document.createElement('button')
				resetBtn.innerHTML = '<i class="dx-icon dx-icon-refresh"></i>'
				resetBtn.className = 'dx-button dx-button-mode-contained dx-button-default reset-password-btn'
				resetBtn.title = 'Reset password'
				resetBtn.style.marginLeft = '5px'
				resetBtn.style.minWidth = '24px'
				resetBtn.style.height = '24px'
				resetBtn.style.padding = '0'
				resetBtn.style.backgroundColor = '#dc3545'
				resetBtn.style.borderColor = '#dc3545'
				resetBtn.style.color = 'white'

				resetBtn.addEventListener('click', event => {
					event.stopPropagation()
					handleResetPassword(e.data)
				})

				// Insert after the edit button if it exists, otherwise append to the cell
				if (editButton) {
					editButton.parentNode.insertBefore(resetBtn, editButton.nextSibling)
				} else {
					actionsCell.appendChild(resetBtn)
				}
			}
		}
	}

	return (
		<DataView classes={classes} icon={`people`} title="Users">
			<DataGrid
				style={{
					width: '100%',
					height: '100%'
				}}
				key="id"
				id="gridContainer"
				dataSource={dataSource}
				renderAsync
				showBorders
				showColumnLines
				allowColumnResizing
				columnResizingMode="widget"
				columnAutoWidth={false}
				columnMinWidth={50}
				columnWidth={150}
				repaintChangesOnly
				onInitNewRow={onInitNewRow}
				onEditingStart={onEditStart}
				onEditCanceled={onEditStop}
				onSaved={onEditStop}
				onRowPrepared={onRowPrepared}
			>
				<GroupPanel visible />
				<Editing mode="form" useIcons allowUpdating allowAdding allowDeleting>
					<Form>
						<Item dataField="UserID" />
						<Item dataField="LastLogin" />
						<Item dataField="Username" />
						<Item dataField="FirstName" />
						<Item dataField="LastName" />
						<Item dataField="FathersName" />
						<Item dataField="JobTitle" />
						<Item dataField="BirthDate" />
						<Item dataField="EmploymentStartDate" />
						<Item dataField="ReportsTo" />
						<Item dataField="IsAzureSSO" />
						<Item dataField="IsPinEnabled" />
						<Item dataField="IsActive" />
						<Item dataField="Is2FAConfigured" />
						<Item dataField="applications" />
						<Item dataField="CompanyID" />
						<Item dataField="DepartmentID" />
					</Form>
				</Editing>
				<HeaderFilter visible />
				<FilterPanel visible />
				<FilterRow visible />
				<RemoteOperations filtering paging sorting summary />
				<Paging defaultPageSize={100} />
				<Pager visible displayMode="full" showInfo />
				<Scrolling mode="virtual" renderAsync rowRenderingMode="virtual" />
				<SearchPanel visible />
				<ColumnChooser enabled allowSearch={false} />
				<Column
					editCellTemplate={cellIDTemplate}
					dataField="UserID"
					dataType="number"
					caption="User"
					width={75}
					fixed
					allowEditing={false}
				/>
				<Column dataField="LastLogin" dataType="date" format={`dd-M-yyyy`} allowEditing={false} />
				<Column dataField="Username" dataType="string">
					<RequiredRule />
					<EmailRule message="Username is not a valid email address" />
					<AsyncRule message="Username has already been used" validationCallback={asyncValidation} />
				</Column>
				<Column dataField="FirstName" dataType="string">
					<RequiredRule />
				</Column>
				<Column dataField="LastName" dataType="string">
					<RequiredRule />
				</Column>
				<Column dataField="FathersName" dataType="string" visible={false} />
				<Column dataField="JobTitle" dataType="string" visible={false} />
				<Column dataField="BirthDate" dataType="date" visible={false} format={`dd-M-yyyy`} />
				<Column dataField="EmploymentStartDate" dataType="date" visible={false} format={`dd-M-yyyy`} />
				<Column dataField="ReportsTo" dataType="integer" visible={false}>
					<Lookup dataSource={users} valueExpr="UserID" displayExpr="Username" />
				</Column>
				<Column dataField="IsAzureSSO" dataType="boolean" caption="Azure Enabled" />
				<Column dataField="IsPinEnabled" dataType="boolean" caption="Pin Enabled" />
				<Column dataField="IsActive" dataType="boolean" caption="Active" />
				<Column
					allowEditing={!isAdding}
					dataField="Is2FAConfigured"
					dataType="boolean"
					caption="2FA Configured"
				/>
				<Column
					dataField="IsLocked"
					dataType="boolean"
					caption="Locked"
					allowEditing={false}
					allowFiltering={false}
					allowSorting={false}
				/>
				<Column
					dataField="applications"
					caption="Applications"
					width={200}
					allowSorting={false}
					allowFiltering={false}
					editCellComponent={ApplicationTagBoxComponent}
					cellTemplate={cellApplicationsTemplate}
				>
					<Lookup dataSource={applications} valueExpr="ApplicationID" displayExpr="ApplicationName" />
					<RequiredRule />
				</Column>
				<Column dataField="CompanyID" caption="Company">
					<Lookup dataSource={companies} valueExpr="CompanyID" displayExpr="CompanyDescription" />
					<RequiredRule />
				</Column>
				<Column dataField="DepartmentID" caption="Department">
					<Lookup dataSource={departments} valueExpr="DepartmentID" displayExpr="DepartmentDescription" />
					<RequiredRule />
				</Column>
				<Column dataField="CreatedBy" caption="Created By" allowEditing={false}>
					<Lookup dataSource={users} valueExpr="UserID" displayExpr="Username" />
				</Column>
				<Column dataField="DeactivatedBy" caption="Deactivated By" allowEditing={false}>
					<Lookup dataSource={users} valueExpr="UserID" displayExpr="Username" />
				</Column>
			</DataGrid>
		</DataView>
	)
}

export default Users
